import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  ButtonBase,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import { SpecialZoomLevel } from "@react-pdf-viewer/core";
import { Dialog } from "components/Dialog";
import { FilePreview } from "components/Document/shared/FilePreview";
import CopyableTypography from "components/Typography/CopyableTypography";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { CompareSourcesQuery, WasteManifestSource } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  COMPARE_SOURCE_ORDER,
  computeCompareFooterCounts,
  computeOutlierSources,
  filterRowsByDifferences,
  getSourceColorKey,
  SOURCE_DOT_COLOR,
  getSourceLabel,
  groupCompareRows,
  MANIFEST_GROUP_LABEL,
  parseCompareParam,
} from "./CompareSourcesDialog.helpers";

export const COMPARE_MANIFEST_SOURCES_QUERY = gql(`
  query CompareSources($processedManifestId: ID!, $wasteLineUnifiedIds: [ID!]) {
    compareManifestSources(
      processedManifestId: $processedManifestId
      wasteLineUnifiedIds: $wasteLineUnifiedIds
    ) {
      availableSources
      differenceCount
      missingCount
      rows {
        key
        group
        field
        label
        eManifest
        vendor
        document
        primarySource
        differs
        missingInSomeSource
      }
    }
  }
`);

export const COMPARE_MANIFEST_PDF_QUERY = gql(`
  query CompareManifestPdf($search: String!) {
    processedManifests(search: $search, page: 0, pageSize: 1) {
      items {
        id
        manifestTrackingNumber
        manifestPdf {
          id
          fileExtension
        }
      }
    }
  }
`);

type CompareRow = CompareSourcesQuery["compareManifestSources"]["rows"][number];

/** Fields whose raw values are ISO dates and should render in our M/D/YYYY format. */
const COMPARE_DATE_FIELDS = new Set(["shippedDate", "receivedDate"]);

/** The stringified value a row holds for a given source column. */
function rowValueForSource(row: CompareRow, source: WasteManifestSource): string | null {
  const raw = (() => {
    switch (source) {
      case WasteManifestSource.EManifest:
        return row.eManifest ?? null;
      case WasteManifestSource.Vendor:
        return row.vendor ?? null;
      case WasteManifestSource.ManifestDocument:
        return row.document ?? null;
    }
  })();
  // Normalize date fields to M/D/YYYY so they don't render as full datetimes
  // (and so equal dates stored in different ISO precisions stop reading as a
  // difference).
  if (raw != null && COMPARE_DATE_FIELDS.has(row.field)) {
    return getUtcDisplayDate(raw);
  }
  return raw;
}

const STICKY_HEADER_HEIGHT = "2.5rem";

interface CompareSourcesDialogProps {
  open: boolean;
  onClose: () => void;
  processedManifestId: string;
  /** When provided, scopes the comparison to these line items (omits the Manifest group). */
  wasteLineUnifiedIds?: string[];
  /** When set, the matching field row is highlighted + scrolled into view on open. */
  highlightField?: string;
}

/** One source column header. Absent sources (no such pillar on the manifest)
 * are muted and labeled so an empty column reads as "not a source here". */
function SourceColumnHeader({
  source,
  borderColor,
  available,
}: {
  source: WasteManifestSource;
  borderColor: string;
  available: boolean;
}) {
  const theme = useTheme();
  // Bright/neon dot color per source (no palette roles for these — see helper).
  const dotColor = SOURCE_DOT_COLOR[getSourceColorKey(source)];
  return (
    <TableCell
      sx={{
        fontWeight: 600,
        borderLeft: `1px solid ${borderColor}`,
        color: available ? undefined : "text.disabled",
      }}
    >
      {/* Dot, label, and "(no data)" sit inline on one row (no wrap), so the
          "(no data)" hint reads to the right of the source name. */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ whiteSpace: "nowrap" }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: available ? dotColor : theme.palette.action.disabled,
            flexShrink: 0,
          }}
        />
        <span>{getSourceLabel(source)}</span>
        {!available && (
          <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 400 }}>
            (no data)
          </Typography>
        )}
      </Stack>
    </TableCell>
  );
}

export function CompareSourcesDialog({
  open,
  onClose,
  processedManifestId,
  wasteLineUnifiedIds,
  highlightField,
}: CompareSourcesDialogProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { tenantId } = useParams<{ tenantId: string }>();

  // Section/column headers get the grey background; data rows are white by
  // default (point: invert the old all-grey table so group starts read clearly).
  const headerBg = isDark ? theme.palette.grey[900] : theme.palette.grey[100];
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const stickyBg = theme.palette.background.paper;
  // Single consistent divider color for the full table grid + outer border.
  const gridBorder = theme.palette.divider;
  // Accent color used to flag divergence: a left spine on a differing row and a
  // dotted underline beneath the specific outlier value(s).
  const diffAccent = theme.palette.warning.main;
  // Pale-orange wash painted on matching cells (and the "on" stat pill) while a
  // stat highlight toggle is active.
  const flashBg = alpha(theme.palette.warning.main, isDark ? 0.32 : 0.2);
  // Opaque highlight tint for the scrolled-to field row (?compare=field:<name>).
  const highlightBg = isDark
    ? darken(theme.palette.primary.main, 0.72)
    : lighten(theme.palette.primary.main, 0.85);

  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  // Which stat is toggled "on": persistently washes the matching cells (the
  // diverging values, or the missing gaps) until toggled off or switched.
  const [highlight, setHighlight] = useState<"differences" | "missing" | null>(null);
  const toggleHighlight = useCallback(
    (kind: "differences" | "missing") => setHighlight((cur) => (cur === kind ? null : kind)),
    []
  );
  const { data, loading } = useQuery(COMPARE_MANIFEST_SOURCES_QUERY, {
    variables: {
      processedManifestId,
      wasteLineUnifiedIds: wasteLineUnifiedIds ?? null,
    },
    skip: !open,
  });

  const { data: pdfData } = useQuery(COMPARE_MANIFEST_PDF_QUERY, {
    variables: { search: `tenantId:${tenantId} id:${processedManifestId}` },
    skip: !open || !tenantId,
  });

  const manifestSummary = pdfData?.processedManifests.items[0] ?? null;
  const manifestPdf = manifestSummary?.manifestPdf ?? null;
  const manifestTrackingNumber = manifestSummary?.manifestTrackingNumber ?? null;
  const showPreviewRail = showPdf && manifestPdf != null;

  // Resolve the manifest PDF's signed URL with the same hook the Documents UI
  // uses, then hand it to the shared <FilePreview> viewer (rather than fetching
  // + rendering a bespoke previewer here).
  const [getDownloadLink] = useDocumentDownloadLink();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    const docId = manifestPdf?.id;
    if (!showPdf || !docId) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    getDownloadLink({ variables: { id: docId } })
      .then((result) => {
        if (!cancelled) setPreviewUrl(result.data?.getDocumentDownloadLink ?? null);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showPdf, manifestPdf?.id, getDownloadLink]);

  const allRows = useMemo<CompareRow[]>(
    () => data?.compareManifestSources.rows ?? [],
    [data?.compareManifestSources.rows]
  );

  // All three source columns are always shown — that's the point of the
  // comparison. This set tracks which pillars actually exist for the manifest
  // so we can tell a real per-field gap ("this source has this field, that one
  // doesn't") apart from an entirely-absent source (no e-Manifest at all). Only
  // the former counts as "missing". Defaults to all three while loading so a
  // value never momentarily reads as an absent-source placeholder.
  const availableSourceSet = useMemo<Set<WasteManifestSource>>(() => {
    const available = data?.compareManifestSources.availableSources;
    if (!available || available.length === 0) return new Set(COMPARE_SOURCE_ORDER);
    return new Set(available);
  }, [data?.compareManifestSources.availableSources]);

  const visibleRows = useMemo(
    () => filterRowsByDifferences(allRows, showOnlyDifferences),
    [allRows, showOnlyDifferences]
  );

  const groupedRows = useMemo(() => groupCompareRows(visibleRows), [visibleRows]);

  // Audit summary counts: prefer the server totals (over the full row set),
  // falling back to a client recompute if the query hasn't resolved yet.
  const summaryCounts = useMemo(() => {
    if (data?.compareManifestSources) {
      return {
        differenceCount: data.compareManifestSources.differenceCount,
        missingCount: data.compareManifestSources.missingCount,
      };
    }
    return computeCompareFooterCounts(allRows);
  }, [data?.compareManifestSources, allRows]);

  // Scroll the highlighted field row into view once data is loaded.
  const highlightedRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (!open || !highlightField || loading) return;
    const node = highlightedRowRef.current;
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [open, highlightField, loading, visibleRows]);

  const openPdfInNewTab = () => {
    if (previewUrl) window.open(previewUrl, "_blank");
  };

  const COL_COUNT = 1 + COMPARE_SOURCE_ORDER.length; // field + all three sources

  const renderValueCell = (row: CompareRow, source: WasteManifestSource, isOutlier: boolean) => {
    const value = rowValueForSource(row, source);
    const hasValue = value != null;
    const sourceAvailable = availableSourceSet.has(source);
    // Two distinct empty states:
    //  - absent source: this manifest has no such pillar at all (its whole
    //    column is blank). Expected, not a data gap — never counted/highlighted.
    //  - gap: an available source simply lacks this field while another has it.
    //    This is what "missing from one or more sources" counts and highlights.
    const isGap = !hasValue && sourceAvailable;
    const isHighlighted =
      (highlight === "differences" && isOutlier) ||
      (highlight === "missing" && isGap && row.missingInSomeSource);
    return (
      <TableCell
        key={source}
        sx={{
          wordBreak: "break-word",
          verticalAlign: "top",
          borderLeft: `1px solid ${gridBorder}`,
          transition: "background-color 0.25s ease",
          ...(isHighlighted ? { bgcolor: flashBg } : {}),
          // Absent-source cells get a faint striped wash so the whole column
          // reads as "not a source here" rather than a field-level gap.
          ...(!sourceAvailable
            ? {
                backgroundImage: `repeating-linear-gradient(45deg, ${gridBorder} 0 1px, transparent 1px 7px)`,
                opacity: 0.6,
              }
            : {}),
        }}
      >
        {hasValue ? (
          // Click-to-copy value. Outliers keep the normal text color (so they
          // stay readable) but are bumped to medium weight and marked with a
          // dotted accent underline — a "spot the difference" cue rather than a
          // flat fill or recolor.
          <CopyableTypography
            text={value}
            variant="body2"
            stopPropagation={false}
            sx={
              isOutlier
                ? {
                    fontWeight: 600,
                    textDecoration: "underline dotted",
                    textDecorationColor: diffAccent,
                    textDecorationThickness: "2px",
                    textUnderlineOffset: "3px",
                  }
                : undefined
            }
          />
        ) : (
          // A gap (available source, no value) shows an em dash; an absent
          // source shows nothing — the striped wash already conveys it.
          <Typography variant="body2" sx={{ color: "text.disabled" }}>
            {sourceAvailable ? "—" : ""}
          </Typography>
        )}
      </TableCell>
    );
  };

  const renderRow = (row: CompareRow) => {
    const isHighlighted = highlightField != null && row.field === highlightField;
    // Background is reserved for the deep-link highlight only; divergence is
    // shown with a left spine + per-value underline, not a row fill.
    const rowBg = isHighlighted ? highlightBg : undefined;

    // Which source(s) hold the diverging value, so we can mark just those cells.
    // Absent sources contribute null and are ignored by the outlier logic.
    const outlierSources = computeOutlierSources(
      COMPARE_SOURCE_ORDER.map((source) => ({ source, value: rowValueForSource(row, source) }))
    );

    return (
      <TableRow
        key={row.key}
        ref={isHighlighted ? highlightedRowRef : undefined}
        sx={{
          ...(rowBg != null ? { bgcolor: rowBg } : {}),
          "& .MuiTableCell-body": { verticalAlign: "top" },
        }}
      >
        <TableCell
          component="th"
          scope="row"
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 1,
            // Field labels stay at normal weight; divergence is flagged by the
            // left accent spine here and the underlined value cells, so the
            // label itself doesn't also need to bold (too much bold otherwise).
            fontWeight: 400,
            ...(showPreviewRail ? {} : { width: 230, whiteSpace: "nowrap" }),
            bgcolor: rowBg ?? stickyBg,
            // Left accent spine flags the whole row as having a divergence,
            // scannable down the left edge of the table.
            ...(row.differs ? { borderLeft: `3px solid ${diffAccent}` } : {}),
          }}
        >
          {row.label}
        </TableCell>
        {COMPARE_SOURCE_ORDER.map((source) =>
          renderValueCell(row, source, outlierSources.has(source))
        )}
      </TableRow>
    );
  };

  const renderPreviewBody = () => {
    if (!previewUrl || !manifestPdf) {
      return <Skeleton variant="rectangular" animation="wave" height="100%" width="100%" />;
    }
    return (
      <FilePreview
        fileUrl={previewUrl}
        fileExtension={manifestPdf.fileExtension ?? "pdf"}
        defaultScale={SpecialZoomLevel.PageFit}
      />
    );
  };

  const titleBarActions = (
    <Stack direction="row" alignItems="center" spacing={1}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={showOnlyDifferences}
            onChange={(e) => setShowOnlyDifferences(e.target.checked)}
          />
        }
        label={<Typography variant="body2">Show only differences</Typography>}
      />
      {manifestPdf && (
        <Tooltip title={showPdf ? "Hide manifest PDF" : "Show manifest PDF"}>
          <IconButton
            size="small"
            onClick={() => setShowPdf((prev) => !prev)}
            color={showPdf ? "primary" : "default"}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  // A single audit stat. When its count is non-zero it's a toggle: clicking it
  // highlights the matching cells in the table and keeps them highlighted until
  // it's clicked again (or the other stat is clicked). The "on" state reads as
  // a filled amber pill.
  const renderStat = (count: number, label: string, kind: "differences" | "missing") => {
    const clickable = count > 0;
    const active = highlight === kind;
    const chip = (
      <ButtonBase
        disabled={!clickable}
        onClick={() => toggleHighlight(kind)}
        focusRipple
        aria-pressed={active}
        sx={{
          borderRadius: 1,
          px: 0.75,
          py: 0.25,
          cursor: clickable ? "pointer" : "default",
          border: "1px solid",
          borderColor: active ? "warning.main" : "transparent",
          bgcolor: active ? flashBg : "transparent",
          transition: "background-color 0.2s ease",
          "&:hover": clickable && !active ? { bgcolor: "action.hover" } : undefined,
        }}
      >
        <Typography
          variant="caption"
          color={active ? "warning.dark" : "text.secondary"}
          component="span"
          sx={{ fontWeight: active ? 600 : undefined }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 700, color: active ? "warning.dark" : "text.primary" }}
          >
            {count}
          </Box>{" "}
          {label}
          {active && " ✓"}
        </Typography>
      </ButtonBase>
    );
    if (!clickable) return chip;
    return (
      <Tooltip title={active ? "Clear highlight" : "Highlight these cells in the table"}>
        {chip}
      </Tooltip>
    );
  };

  // Quiet inline audit summary, rendered right below the dialog title.
  const auditSummary = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 0, pt: 0.5, pb: 0.25, flexShrink: 0 }}
    >
      {renderStat(
        summaryCounts.differenceCount,
        summaryCounts.differenceCount === 1 ? "difference" : "differences",
        "differences"
      )}
      {renderStat(summaryCounts.missingCount, "missing from one or more sources", "missing")}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullScreen}
      noPrimaryAction
      title={
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
          <span>
            {wasteLineUnifiedIds
              ? "Compare Manifest Data Sources — Line Items"
              : "Compare Manifest Data Sources"}
          </span>
          {manifestTrackingNumber && (
            <Box
              component="span"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                fontWeight: 400,
                color: "text.secondary",
                letterSpacing: "0.02em",
              }}
            >
              {manifestTrackingNumber}
            </Box>
          )}
        </Box>
      }
      titleBarActionElement={titleBarActions}
      contentSx={{ p: 0, overflow: "hidden" }}
      slotProps={{
        paper: { sx: { height: fullScreen ? "100%" : "90vh", maxHeight: "90vh" } },
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {auditSummary}
        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "row", minHeight: 0, overflow: "hidden" }}
        >
          <Box sx={{ flex: 3, minWidth: 0, overflow: "auto" }}>
            {loading ? (
              <Stack spacing={1} sx={{ p: 2 }}>
                {Array.from({ length: 8 }, (_, i) => (
                  <Skeleton key={i} variant="rectangular" height={32} />
                ))}
              </Stack>
            ) : (
              <TableContainer sx={{ overflow: "visible", px: 0, pt: 0, pb: 2 }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    border: `1px solid ${gridBorder}`,
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    // Fixed layout: the Field column takes a fixed width and the
                    // three source columns split the rest equally — so they stay
                    // equal even when a source has no data.
                    width: "100%",
                    tableLayout: "fixed",
                    // Single header divider, flush against the first row (no gap / double line).
                    "& .MuiTableHead-root .MuiTableCell-head": {
                      top: 0,
                      height: STICKY_HEADER_HEIGHT,
                      bgcolor: headerBg,
                      borderBottom: `1px solid ${gridBorder}`,
                    },
                    "& .MuiTableCell-root": { borderBottom: `1px solid ${gridBorder}` },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          // With the PDF rail open, space is tight — let the Field
                          // column collapse to an equal share like the rest.
                          ...(showPreviewRail ? {} : { width: 230, whiteSpace: "nowrap" }),
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          bgcolor: headerBg,
                        }}
                      >
                        Field
                      </TableCell>
                      {COMPARE_SOURCE_ORDER.map((source) => (
                        <SourceColumnHeader
                          key={source}
                          source={source}
                          borderColor={gridBorder}
                          available={availableSourceSet.has(source)}
                        />
                      ))}
                    </TableRow>
                  </TableHead>
                  {groupedRows.map((groupBlock) => (
                    <TableBody key={groupBlock.group}>
                      {/* The manifest-level fields lead the table un-headered; only
                          the per-line-item groups get a section header row. */}
                      {groupBlock.group !== MANIFEST_GROUP_LABEL && (
                        <TableRow>
                          <TableCell
                            colSpan={COL_COUNT}
                            sx={{
                              bgcolor: headerBg,
                              py: 0.5,
                              position: "sticky",
                              top: STICKY_HEADER_HEIGHT,
                              zIndex: 2,
                              // Stronger top rule sets each line item apart from
                              // the block above it.
                              borderTop: `2px solid ${gridBorder}`,
                            }}
                          >
                            {/* Section headers read as overline labels —
                                deliberately unlike the column-header row so
                                "Line 1" can't be mistaken for a field/column
                                heading. */}
                            <Typography
                              variant="overline"
                              sx={{
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                lineHeight: 1.4,
                                color: "text.secondary",
                              }}
                            >
                              {groupBlock.group}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {groupBlock.rows.map(renderRow)}
                    </TableBody>
                  ))}

                  {visibleRows.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={COL_COUNT}
                          align="center"
                          sx={{ py: 8, bgcolor: subtleSurfaceBg }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {showOnlyDifferences
                              ? "No differences between sources"
                              : "No comparison data available"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            )}
          </Box>

          {showPreviewRail && (
            <>
              <Box
                sx={{
                  // 40% to the PDF viewer (60% to the data-source table).
                  flex: 2,
                  minWidth: 0,
                  // minHeight:0 lets this column shrink to the available height
                  // so the PDF viewer fits instead of pushing past the modal.
                  minHeight: 0,
                  // Bottom inset matching the viewer's right margin so the PDF
                  // doesn't sit flush against the dialog's bottom edge.
                  pb: 3,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    height: STICKY_HEADER_HEIGHT,
                    flexShrink: 0,
                    borderTop: `1px solid ${gridBorder}`,
                    borderLeft: `1px solid ${gridBorder}`,
                    borderRight: `1px solid ${gridBorder}`,
                    // No bottom border: the PDF viewer below has its own full
                    // outline, so its top edge is the single separator (avoids a
                    // doubled 2px line under this header).
                    bgcolor: headerBg,
                  }}
                >
                  <Typography variant="subtitle2">Manifest PDF</Typography>
                  <Stack direction="row" spacing={0.5}>
                    {previewUrl && (
                      <Tooltip title="Open in new tab">
                        <IconButton size="small" onClick={openPdfInNewTab}>
                          <OpenInNewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Hide PDF">
                      <IconButton size="small" onClick={() => setShowPdf(false)}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {/* Pin the viewer to this box so the PDF's intrinsic height can't
                    stretch the rail past the modal. */}
                <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
                  <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
                    {renderPreviewBody()}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

/** The `?compare=` URL param key this permalink surface reads/writes. */
export const COMPARE_PARAM_KEY = "compare";

interface CompareSourcesPermalinkProps {
  /**
   * The manifest the `?compare=` permalink resolves against. The permalink only
   * opens when this matches the route's current manifest, so the host renders
   * it scoped to the manifest in view.
   */
  processedManifestId: string;
}

/**
 * URL-driven open surface for {@link CompareSourcesDialog}. Reads the
 * `?compare=` search param and opens the dialog accordingly:
 *
 * - `compare=open` — full-manifest comparison.
 * - `compare=line:<wluId>` — scoped to a single line item.
 * - `compare=field:<fieldName>` — full-manifest comparison with that field row
 *   highlighted and scrolled into view.
 *
 * Closing the dialog clears the param. Mount this once on the manifest route
 * (call sites that open the dialog directly should render
 * {@link CompareSourcesDialog} themselves).
 */
export function CompareSourcesPermalink({ processedManifestId }: CompareSourcesPermalinkProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  // Staff-only, matching the in-drawer compare entry points — keeps the
  // `?compare=` deep-link inert for non-staff rather than gating only the button.
  const compareEnabled = isStaff && !!featureFlags[FeatureFlagKey.WasteManifestDiscrepancies];
  const parsed = useMemo(
    () => parseCompareParam(searchParams.get(COMPARE_PARAM_KEY)),
    [searchParams]
  );

  const handleClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(COMPARE_PARAM_KEY);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  if (!parsed || !compareEnabled) return null;

  const wasteLineUnifiedIds = parsed.mode === "line" ? [parsed.wasteLineUnifiedId] : undefined;
  const highlightField = parsed.mode === "field" ? parsed.field : undefined;

  return (
    <CompareSourcesDialog
      open
      onClose={handleClose}
      processedManifestId={processedManifestId}
      wasteLineUnifiedIds={wasteLineUnifiedIds}
      highlightField={highlightField}
    />
  );
}
